import { makeid } from 'utils/helpers/makeid';

import {
  booleanOptions,
  notEscalatedOptions,
  nursingInterventionOptions,
  commonFactors,
} from './common';

import type { TFormItem, IFormItemOption } from '../types';

export const hyperglycemiaSymptoms: IFormItemOption<string>[] = [
  {
    label: 'Fruity-smelling breath',
    value: 'breath',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Nausea/vomiting/diarrhea',
    value: 'nauseaVomitingDiarrhea',
    required: true,
    key: makeid(10),
  },
  { label: 'Abdominal pain', value: 'pain', required: true, key: makeid(10) },
  {
    label: 'Shortness of breath',
    value: 'shortnessOfBreath',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Confusion or loss of consciousness',
    value: 'confusionLossConsciousness',
    required: true,
    key: makeid(10),
  },
  { label: 'Headaches', value: 'headaches', key: makeid(10) },
  { label: 'Blurred vision', value: 'blurredVision', key: makeid(10) },
  { label: 'Fatigue', value: 'fatigue', key: makeid(10) },
  { label: 'Frequent urination', value: 'frequentUrination', key: makeid(10) },
  { label: 'Excessive thirst', value: 'excessiveThirst', key: makeid(10) },
  { label: 'Weight loss', value: 'weightLoss', key: makeid(10) },
];

export const hypoglycemiaSymptoms: IFormItemOption<string>[] = [
  {
    label: 'Mental confusion/unresponsiveness',
    value: 'confusion',
    required: true,
    key: makeid(10),
  },
  { label: 'Seizures', value: 'seizures', required: true, key: makeid(10) },
  {
    label: 'Weakness/incoordination',
    value: 'weaknessIncoordination',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Inability to eat or drink',
    value: 'inabilityEatDrin',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Difficulty speaking',
    value: 'difficultySpeaking',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Dizziness/lightheadedness',
    value: 'dizziness',
    required: true,
    key: makeid(10),
  },
  { label: 'Increased hunger', value: 'increaseHunger', key: makeid(10) },
  { label: 'Shakiness', value: 'shakiness', key: makeid(10) },
  {
    label: 'Clammy skin/sweating',
    value: 'clammySkinSweating',
    key: makeid(10),
  },
  { label: 'Sleepiness', value: 'sleepiness', key: makeid(10) },
];

export const factorsAssessment: IFormItemOption<string>[] = [
  ...commonFactors,
  {
    label: 'Recent steroid use (oral or inhaled)',
    value: 'recentSteroid',
    key: makeid(10),
  },
  {
    label: 'Device needs QC',
    value: 'needQc',
    key: makeid(10),
  },
];

export const assessmentBloodGlucoseForm: TFormItem[] = [
  {
    title: '#1 Did you speak to patient?',
    description:
      'Choose other if unlisted family member & type in their relationship. (Ex: Daughter in law) Add Name of Individual you spoke to in other.',
    type: 'radio',
    required: true,
    name: 'patient',
    key: makeid(10),
    options: [
      { value: 'Patient', label: 'Patient', key: makeid(10) },
      { value: 'Spouse', label: 'Spouse', key: makeid(10) },
      { value: 'Caregiver', label: 'Caregiver', key: makeid(10) },
      {
        value: 'Emergency Contact',
        label: 'Emergency Contact',
        key: makeid(10),
      },
      { value: null, label: 'Other', key: makeid(10) },
    ],
    extra: [
      {
        type: 'doubleRowInput',
        name: 'other',
        key: makeid(10),
        options: [
          { label: 'Other Member', value: 'other', key: makeid(10) },
          { label: 'Relationship', value: 'relationship', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#2 Mental Status Assessment',
    description:
      'Oriented and Coherent 1: Person 2: Person, place 3: Person, place, time 4: Person, place, time, situation?',
    type: 'radio',
    required: false,
    name: 'AOC',
    key: makeid(10),
    options: [
      { value: 'x1', label: 'x1', key: makeid(10) },
      { value: 'x2', label: 'x2', key: makeid(10) },
      { value: 'x3', label: 'x3', key: makeid(10) },
      { value: 'x4', label: 'x4', key: makeid(10) },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#3 Appointments',
    type: 'datepicker',
    name: 'datepicker',
    required: false,
    key: makeid(10),
    options: [
      {
        value: 'last',
        label: "Last Doctor's Appointment",
        type: 'max',
        key: makeid(10),
      },
      {
        value: 'next',
        label: "Next Doctor's Appointment",
        type: 'min',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#4 Patient is able to take another reading at time of call?',
    subtitle: 'Is the reading still out of bounds?',
    type: 'radio',
    required: true,
    name: 'isOneMoreReading',
    key: makeid(10),
    options: [
      { label: 'Yes', value: 'true', key: makeid(10) },
      { label: 'No', value: 'false', key: makeid(10) },
    ],
    extra: [
      {
        type: 'radio',
        name: 'bounds',
        label: 'Is the reading still out of bounds?',
        key: makeid(10),
        options: booleanOptions,
        divider: true,
      },
      {
        type: 'input',
        name: 'repeatReadingWas',
        key: makeid(10),
        options: [
          {
            label: 'The repeat reading was:',
            value: 'repeatReadingWas',
            key: makeid(10),
          },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#5 Signs and Symptoms of HYPERglycemia',
    type: 'radio-list',
    name: 'symptoms',
    required: true,
    key: makeid(10),
    options: hyperglycemiaSymptoms,
    extra: [
      {
        name: 'symptomsOnset',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['max'],
  },
  {
    title: '#5 Signs and Symptoms of HYPOglycemia',
    type: 'radio-list',
    name: 'symptoms',
    required: true,
    key: makeid(10),
    options: hypoglycemiaSymptoms,
    extra: [
      {
        name: 'symptomsOnset',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min'],
  },
  {
    title: '#6 Did patient take routine medications as prescribed?',
    type: 'radio',
    required: false,
    name: 'isTakeMedications',
    key: makeid(10),
    options: [
      { label: 'Yes', value: 'true', key: makeid(10) },
      { label: 'No', value: 'false', key: makeid(10) },
    ],
    extra: [
      {
        type: 'checkbox',
        name: 'prescribed',
        key: makeid(10),
        options: [
          { value: 'oral', label: 'Oral antidiabetic', key: makeid(10) },
          {
            value: 'injectable',
            label: 'Antidiabetic injectable',
            key: makeid(10),
          },
          {
            value: 'insulinLong',
            label: 'Insulin - long acting',
            key: makeid(10),
          },
          {
            value: 'insulinShort',
            label: 'Insulin - short acting',
            key: makeid(10),
          },
          { value: 'insulinPump', label: 'Insulin - Pump', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#7 Was blood sugar fasting or non-fasting?',
    type: 'radio',
    required: false,
    name: 'bloodSugar',
    key: makeid(10),
    options: [
      { value: 'fasting', label: 'Fasting', key: makeid(10) },
      { value: 'non-fasting', label: 'Non-fasting', key: makeid(10) },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#8 Factors Assessment',
    type: 'radio-list',
    required: false,
    key: makeid(10),
    options: factorsAssessment,
    name: 'factorsAssessment',
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#9 Additional Details',
    type: 'text',
    required: false,
    name: 'details',
    key: makeid(10),
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#10 Nursing Intervention',
    type: 'checkbox',
    name: 'nursingIntervention',
    required: true,
    key: makeid(10),
    options: nursingInterventionOptions,
    extra: [
      {
        type: 'labelCheckbox',
        name: 'notEscalated',
        key: makeid(10),
        label: 'Not Escalated',
        options: notEscalatedOptions,
        divider: true,
      },
      {
        name: 'interventionDetails',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    title: '#11 Patient Understanding',
    description:
      'Patient verbalized understanding and is agreeable to nursing recommendation',
    type: 'radio',
    required: false,
    name: 'verbalized',
    key: makeid(10),
    options: booleanOptions,
    readingTypeFor: ['min', 'max'],
  },
];
